/**
 * OFAC status — canonical type + back-compat normalization.
 *
 * Bill 2026-05-09 — OFAC archive-match suppression. Today's writers stamp
 * `'hit'` when JHA OFACSrch returns ≥1 match, regardless of whether the
 * match is on Treasury's CURRENT sanctions list or just a historical/
 * delisted record. With the Wave-3 SDN cache classifying each AliasId
 * (`current` / `archived-only` / `unknown`), `'hit'` splits into:
 *
 *   - `'hit-current'`  — at least one match is on the active SDN list →
 *                        block submit, escalate to Risk Management.
 *   - `'archived-only'`— every match was archived/delisted by Treasury →
 *                        allow onboarding to continue, audit-logged.
 *
 * `'hit'` (legacy literal) is preserved as a back-compat read shim so any
 * cart written before this change is still treated as "blocked / current".
 *
 * Pair with `extra.ofacClassifications: OfacClassificationRecord[]` for the
 * per-match enrichment (programs, primaryName, AKA detail, etc.).
 */

/**
 * Canonical OFAC rollup status. Persisted on `person.extra.ofacStatus`.
 *
 *   - `'unrun'`         — no scan attempted yet (or unknown legacy value).
 *   - `'pending'`       — scan in flight or backend retry queued.
 *   - `'clear'`         — JHA returned 0 matches.
 *   - `'archived-only'` — JHA returned ≥1 match, ALL classified as
 *                         historical/delisted by the SDN cache. Submit
 *                         allowed; visible in audit trail.
 *   - `'hit-current'`   — JHA returned ≥1 match, ≥1 classified as on the
 *                         current SDN list (or `'unknown'` cache result,
 *                         fail-safe to current). Block submit, escalate
 *                         to Risk Management.
 */
export type OfacStatus =
  | 'unrun'
  | 'pending'
  | 'clear'
  | 'archived-only'
  | 'hit-current';

/**
 * Per-match enrichment row persisted on `person.extra.ofacClassifications`.
 * Subset of the api-client `OfacClassification` shape — only what the FSR
 * UI surfaces or the Risk Mgmt audit needs to read back.
 */
export interface OfacClassificationRecord {
  aliasId: string;
  classification: 'current' | 'archived-only' | 'unknown';
  isCurrentSdn: boolean;
  entityId: string | null;
  primaryName: string | null;
  programs: ReadonlyArray<string>;
  akaName: string | null;
  akaType: string | null;
}

/**
 * Back-compat read shim. Normalizes any persisted-or-runtime ofacStatus
 * literal (including the legacy `'hit'` written by carts created before
 * the archive-match suppression rollout) into the canonical {@link OfacStatus}
 * union. Apply this at every read site of `person.extra.ofacStatus`.
 *
 * Migration table:
 *   'hit'                    → 'hit-current'  (legacy → fail-safe block)
 *   'no_hit' | 'cleared'     → 'clear'         (legacy variants)
 *   'idle' | 'running'       → 'pending'       (legacy SPFx values)
 *   'error'                  → 'pending'       (treat as still-running)
 *   'Cleared' | 'Pending' |  → toLowerCase →   (TitleCase aliases)
 *     'Hit'                    'hit-current' / 'pending' / 'clear'
 *   anything else            → 'unrun'
 */
export function normalizeOfacStatus(raw: string | undefined | null): OfacStatus {
  if (!raw) return 'unrun';
  const v = String(raw).trim().toLowerCase();
  if (v === '') return 'unrun';
  // Canonical post-2026-05-09 values.
  if (v === 'unrun') return 'unrun';
  if (v === 'pending') return 'pending';
  if (v === 'clear') return 'clear';
  if (v === 'archived-only' || v === 'archived_only' || v === 'archivedonly') {
    return 'archived-only';
  }
  if (v === 'hit-current' || v === 'hit_current' || v === 'hitcurrent') {
    return 'hit-current';
  }
  // Legacy variants → migrate.
  if (v === 'no_hit' || v === 'cleared') return 'clear';
  if (v === 'hit') return 'hit-current';
  if (v === 'idle' || v === 'running') return 'pending';
  if (v === 'error') return 'pending';
  return 'unrun';
}

/**
 * True when the cart-submit gate must hard-block on this status. Used by
 * ProceduralGates + complianceRulesEngine + any other gate consumer.
 *
 * NOTE: `pending` and `unrun` are NOT blocked here — they have their own
 * dedicated gate buckets in ProceduralGates.buildOfacGate (one warns, one
 * fails). This helper exists for the binary "is this a hard sanctions
 * block" question.
 */
export function isOfacHardBlock(status: OfacStatus): boolean {
  return status === 'hit-current';
}

/**
 * True when the status represents a successful OFAC clearance from the
 * cart-submit perspective. Includes `archived-only` because Treasury has
 * removed those records from the active sanctions list — onboarding may
 * continue, with the match logged for Risk Management visibility.
 */
export function isOfacSubmitAllowed(status: OfacStatus): boolean {
  return status === 'clear' || status === 'archived-only';
}
